"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";

/* ── Données témoignages ── */
const testimonials = [{
  id: 1,
  name: "Fabien Lanau",
  role: "Directeur",
  company: "CFV",
  companyUrl: "https://www.cfv-formation.com",
  text: "Dans un contexte exigeant, nous avons confié à Christelle Charrier-Robelin l'accompagnement à l'enregistrement de nos certifications, au Répertoire Spécifique et au RNCP.\nSon intervention a permis de sécuriser l'ensemble du processus.\nLes résultats obtenus sont à la hauteur des exigences fixées :\n\u2022\u00A0un renouvellement pour 5 ans au Répertoire Spécifique ;\n\u2022\u00A0un enregistrement RNCP pour 3 ans dès la première présentation, malgré une seule promotion disponible.\nNous avons également pu intégrer une approche innovante en VAE, combinant production écrite et preuves vidéo, parfaitement alignée avec la réalité des compétences attendues.\nCet accompagnement se distingue par sa rigueur, sa capacité d'anticipation et une exigence constante orientée vers un objectif clair : obtenir la durée d'enregistrement maximale.",
  rating: 5
}, {
  id: 2,
  name: "Nicolas Foucher",
  role: "Associé",
  company: "Pierre Morize Conseil (PMC)",
  companyUrl: "https://www.pm-conseil.fr",
  text: "Nous avons confié à Christelle Charrier-Robelin l'accompagnement à l'enregistrement au Répertoire Spécifique de notre certification Faciliter l'intelligence collective.\nSa lecture du décret n°\u00A02025-500 et de la doctrine France Compétences est précise, sans approximation. Le dossier a été construit avec nous, à partir de notre pratique réelle.\nRésultat : enregistrement obtenu pour 3 ans !",
  rating: 5
}
// Ajoutez les prochains témoignages ici
];

/* ── Carte témoignage ── */
function TestimonialCard({
  testimonial
}: {
  testimonial: (typeof testimonials)[0];
}) {
  return <div className="bg-white/10 backdrop-blur-sm rounded-sm shadow-lg border border-white/15 overflow-hidden h-full">
      {/* Barre supérieure rose pâle */}
      <div className="bg-[hsl(343,60%,70%)]/40 px-6 py-3 flex items-center justify-between">
        <Quote className="size-5 text-[hsl(343,60%,80%)]" />
        <div className="flex gap-0.5">
          {Array.from({
          length: testimonial.rating
        }).map((_, i) => <Star key={i} className="size-4 text-[hsl(343,60%,80%)] fill-[hsl(343,60%,80%)]" />)}
        </div>
      </div>

      <div className="p-6 md:p-8">
        {/* Verbatim intégral */}
        <p className="font-serif italic text-white/90 text-base leading-relaxed mb-6 whitespace-pre-line">
          «&nbsp;{testimonial.text}&nbsp;»
        </p>

        {/* Séparateur */}
        <div className="h-px bg-white/20 mb-4" />

        {/* Auteur */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(343,60%,70%)]/20 border border-[hsl(343,60%,70%)]/30 flex items-center justify-center">
            <span className="text-[hsl(343,60%,80%)] font-bold text-sm">
              {testimonial.name.split(" ").map(n => n[0]).join("")}
            </span>
          </div>
          <div>
            <p className="font-semibold text-white text-sm">
              {testimonial.name}
            </p>
            <p className="text-xs text-white/60">
              {testimonial.role},{" "}
              {testimonial.companyUrl ? <a href={testimonial.companyUrl} target="_blank" rel="noopener noreferrer" className="text-[hsl(343,60%,80%)] hover:underline">
                  {testimonial.company}
                </a> : testimonial.company}
            </p>
          </div>
        </div>
      </div>
    </div>;
}

/* ── Section Témoignages ── */
export default function Testimonials() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const totalSlides = testimonials.length;
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => prev === 0 ? totalSlides - 1 : prev - 1);
  }, [totalSlides]);
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => prev === totalSlides - 1 ? 0 : prev + 1);
  }, [totalSlides]);
  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Défilement automatique toutes les 8 secondes (suspendu au survol)
  useEffect(() => {
    if (isPaused || totalSlides <= 1) return;
    const timer = setInterval(() => {
      goToNext();
    }, 8000);
    return () => clearInterval(timer);
  }, [isPaused, totalSlides, goToNext]);

  // Navigation clavier (flèches gauche/droite)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPrevious, goToNext]);
  return <section id="temoignages" className="w-full bg-[hsl(343,50%,18%)] py-14 md:py-20">
      <div className="container mx-auto px-4 md:px-6">
        {/* En-tête */}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-12 transition-all duration-800 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <span className="inline-block text-[hsl(343,60%,75%)] font-serif text-sm tracking-widest uppercase mb-3">
            Témoignages
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-normal leading-tight mb-4">
            Ce que disent nos clients
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Des résultats concrets, obtenus dans des contextes exigeants, grâce
            à un accompagnement rigoureux et co-construit.
          </p>
        </div>

        {/* Carrousel de témoignages */}
        <div ref={carouselRef} className={cn("max-w-4xl mx-auto relative transition-all duration-700 ease-out", isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} role="region" aria-roledescription="carrousel" aria-label="Témoignages clients">
          {/* Conteneur des slides avec overflow caché */}
          <div className="overflow-hidden">
            <div className="flex transition-transform duration-700 ease-in-out" style={{
            transform: `translateX(-${currentIndex * 100}%)`
          }}>
              {testimonials.map((testimonial, index) => <div key={testimonial.id} className="w-full flex-shrink-0 px-1" role="group" aria-roledescription="diapositive" aria-label={`Témoignage ${index + 1} sur ${totalSlides}`} aria-hidden={currentIndex !== index}>
                  <TestimonialCard testimonial={testimonial} />
                </div>)}
            </div>
          </div>

          {/* Boutons de navigation (affichés seulement s'il y a plus d'un témoignage) */}
          {totalSlides > 1 && <>
              <button onClick={goToPrevious} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 size-10 md:size-12 rounded-full bg-white/10 hover:bg-[hsl(343,60%,70%)]/40 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-all duration-200 z-10" aria-label="Témoignage précédent">
                <ChevronLeft className="size-5 md:size-6" />
              </button>
              <button onClick={goToNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 size-10 md:size-12 rounded-full bg-white/10 hover:bg-[hsl(343,60%,70%)]/40 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-all duration-200 z-10" aria-label="Témoignage suivant">
                <ChevronRight className="size-5 md:size-6" />
              </button>
            </>}

          {/* Indicateurs (points) */}
          {totalSlides > 1 && <div className="flex items-center justify-center gap-2 mt-8" role="tablist">
              {testimonials.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={cn("h-2 rounded-full transition-all duration-300", currentIndex === index ? "w-8 bg-[hsl(343,60%,80%)]" : "w-2 bg-white/30 hover:bg-white/50")} role="tab" aria-selected={currentIndex === index} aria-label={`Aller au témoignage ${index + 1}`} />)}
            </div>}
        </div>
      </div>
    </section>;
}